import { __awaiter } from "tslib";
import { UTCDateTimeStringToDateString } from '../_internal/conversion/converters/UTCDateTimeStringToDate';
import { convert } from '../_internal/conversion/module';
import { CompanionError } from '../_internal/error/CompanionError';
import { validateStringArgumentNotEmpty } from '../_internal/validation';
import * as api from '../api/module';
import { paramsToString, parseRequestOptions } from '../api/util';
import { fileToBase64 } from './util';
const drmConversionConfig = {
    created: UTCDateTimeStringToDateString,
    last_modified: UTCDateTimeStringToDateString,
};
/**
 * List DRM policies for a site
 */
export function listDRMPolicies(params, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        const query = paramsToString(params);
        const res = yield api.get(`/v2/sites/${requestOptions.siteId}/drm_policies/${query}`, requestOptions);
        return {
            raw: res,
            content: Object.assign(Object.assign({}, res.data), { drm_policies: res.data.drm_policies.map((i) => convert('toClient', i, drmConversionConfig)) }),
        };
    });
}
/**
 * Create a new DRM policy for a site
 */
export function createDRMPolicy(data, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        const res = yield api.post(`/v2/sites/${requestOptions.siteId}/drm_policies/`, Object.assign(Object.assign({}, requestOptions), { data: {
                metadata: data.metadata,
            } }));
        return {
            raw: res,
            content: convert('toClient', res.data, drmConversionConfig),
        };
    });
}
/**
 * Update an existing DRM policy
 */
export function updateDRMPolicy(id, data, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        validateStringArgumentNotEmpty(id, 'id');
        const res = yield api.patch(`v2/sites/${requestOptions.siteId}/drm_policies/${id}`, Object.assign(Object.assign({}, requestOptions), { data: {
                metadata: data.metadata,
            } }));
        return {
            raw: res,
            content: convert('toClient', res.data, drmConversionConfig),
        };
    });
}
/**
 * Get an existing DRM policy
 */
export function getDRMPolicy(id, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        validateStringArgumentNotEmpty(id, 'id');
        const res = yield api.get(`/v2/sites/${requestOptions.siteId}/drm_policies/${id}`, requestOptions);
        return {
            raw: res,
            content: convert('toClient', res.data, drmConversionConfig),
        };
    });
}
/**
 * Delete an existing DRM policy
 */
export function deleteDRMPolicy(id, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        validateStringArgumentNotEmpty(id, 'id');
        const res = yield api.remove(`v2/sites/${requestOptions.siteId}/drm_policies/${id}`);
        return {
            raw: res,
            content: res.data,
        };
    });
}
/**
 * Check if DRM is enabled for site
 */
export function isDRMEnabled(requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        const res = yield api.get(`/v2/sites/${requestOptions.siteId}/is_drm_enabled/`);
        return {
            raw: res,
            content: res.data,
        };
    });
}
/**
 * Store fairplay credentials on a DRM-enabled site
 */
export function storeFairplayCertificate(data, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        if (typeof data.pkcs12 !== 'string') {
            try {
                const { content, mimetype, raw } = yield fileToBase64(data.pkcs12);
                // Check if mimetype is valid
                if (mimetype !== 'data:application/x-pkcs12') {
                    throw new Error(raw);
                }
                // Overwrite property in data object with base64 string
                data.pkcs12 = content;
            }
            catch (e) {
                throw new CompanionError({
                    error: e,
                    code: 'PKCS_FILE_INVALID',
                });
            }
        }
        const res = yield api.put(`/v2/sites/${requestOptions.siteId}/fairplay_streaming/store_certificate`, Object.assign(Object.assign({}, requestOptions), { data }));
        return {
            raw: res,
            content: res.data,
        };
    });
}
storeFairplayCertificate.ERR = {
    PKCS_FILE_INVALID: 'PKCS_FILE_INVALID',
};
/**
 * Get the DRM Content ID for the media
 */
export function getDrmContentId(id, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        const res = yield api.get(`/v2/sites/${requestOptions.siteId}/media/${id}/content_id`);
        return {
            raw: res,
            content: res.data,
        };
    });
}
